import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Folder, FolderState } from '../types/folder';
import { useAuthStore } from './authStore';
import { supabase } from '../utils/supabase';

// Helper function to validate UUID format
const isValidUUID = (uuid: string): boolean => {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return uuidRegex.test(uuid);
};

export const useFolderStore = create<FolderState>()(
  persist(
    (set, get) => ({
      folders: [],
      currentFolderId: null,

      createDefaultFolder: async () => {
        const user = useAuthStore.getState().user;
        if (!user) return;

        try {
          // Check if user already has folders
          const { data, error } = await supabase
            .from('folders')
            .select('id')
            .eq('user_id', user.id)
            .limit(1);

          if (error) throw error;

          // Only create default folder if user has no folders
          if (!data || data.length === 0) {
            // Insert default folder
            const { data: folderData, error: folderError } = await supabase
              .from('folders')
              .insert({
                name: '基本フォルダ',
                user_id: user.id,
                order: 0,
                description: 'デフォルトフォルダ',
                created_at: new Date().toISOString()
              })
              .select()
              .single();

            if (folderError) throw folderError;

            // Add to local state
            set(state => ({
              folders: [...state.folders, {
                id: folderData.id,
                name: folderData.name,
                createdAt: new Date(folderData.created_at),
                userId: folderData.user_id,
                order: folderData.order || 0
              }],
              currentFolderId: folderData.id // Set as current folder
            }));
          }
        } catch (error) {
          console.error('Failed to create default folder:', error);
        }
      },

      loadFolders: async () => {
        const user = useAuthStore.getState().user;
        if (!user) return;

        // Validate that user.id is a proper UUID
        if (!isValidUUID(user.id)) {
          console.error('Invalid user ID format:', user.id);
          // Clear invalid user state and logout
          await useAuthStore.getState().logout();
          return;
        }

        try {
          // Fetch folders from Supabase
          const { data, error } = await supabase
            .from('folders')
            .select('*')
            .eq('user_id', user.id)
            .order('order', { ascending: true });

          if (error) throw error;

          // Convert from snake_case to camelCase
          const folders = data?.map(f => ({
            id: f.id,
            name: f.name,
            createdAt: new Date(f.created_at),
            userId: f.user_id,
            order: f.order || 0
          })) || [];

          set({ folders });
          
          // If no folders exist, create a default folder
          if (folders.length === 0) {
            get().createDefaultFolder();
          } else {
            // Check if the persisted currentFolderId is valid
            const persistedFolderId = get().currentFolderId;
            const folderExists = persistedFolderId && folders.some(f => f.id === persistedFolderId);
            
            if (folderExists) {
              // Keep the persisted folder selection if it exists in the loaded folders
              // No need to set currentFolderId as it's already restored from persistence
            } else {
              // If persisted folder doesn't exist or is null, select the first folder as current
              set({ currentFolderId: folders[0].id });
            }
          }
        } catch (error) {
          console.error('Failed to load folders:', error);
        }
      },

      createFolder: async (name: string) => {
        const user = useAuthStore.getState().user;
        if (!user) return undefined;

        // Validate that user.id is a proper UUID
        if (!isValidUUID(user.id)) {
          console.error('Invalid user ID format:', user.id);
          await useAuthStore.getState().logout();
          return undefined;
        }

        try {
          const folders = get().folders;
          const newOrder = folders.length;

          // Insert into Supabase
          const { data, error } = await supabase
            .from('folders')
            .insert({
              name,
              user_id: user.id,
              order: newOrder,
              created_at: new Date().toISOString()
            })
            .select()
            .single();

          if (error) throw error;

          const newFolder: Folder = {
            id: data.id,
            name: data.name,
            createdAt: new Date(data.created_at),
            userId: data.user_id,
            order: data.order || 0
          };

          set(state => ({
            folders: [...state.folders, newFolder],
            currentFolderId: newFolder.id
          }));
          
          return newFolder.id;
        } catch (error) {
          console.error('Failed to create folder:', error);
          return undefined;
        }
      },

      deleteFolder: async (id: number) => {
        const user = useAuthStore.getState().user;
        if (!user) return;

        // Validate that user.id is a proper UUID
        if (!isValidUUID(user.id)) {
          console.error('Invalid user ID format:', user.id);
          await useAuthStore.getState().logout();
          return;
        }

        try {
          // Delete from Supabase
          const { error } = await supabase
            .from('folders')
            .delete()
            .eq('id', id);

          if (error) throw error;

          // Update products in the deleted folder
          await supabase
            .from('products')
            .update({ folder_id: null })
            .eq('folder_id', id)
            .eq('user_id', user.id);

          set(state => ({
            folders: state.folders.filter(f => f.id !== id),
            currentFolderId: state.currentFolderId === id ? null : state.currentFolderId
          }));
        } catch (error) {
          console.error('Failed to delete folder:', error);
        }
      },

      setCurrentFolder: (id: number | null) => {
        set({ currentFolderId: id });
      },

      getDefaultFolderId: () => {
        const { folders } = get();
        const kiso = folders.find(f => f.name === '基本フォルダ');
        return kiso?.id ?? folders[0]?.id;
      },

      resolveDefaultFolderId: async () => {
        let id = get().getDefaultFolderId();
        if (!id) {
          await get().createDefaultFolder();
          id = get().getDefaultFolderId();
        }
        return id;
      },

      reorderFolders: async (fromIndex: number, toIndex: number) => {
        const user = useAuthStore.getState().user;
        if (!user) return;

        // Validate that user.id is a proper UUID
        if (!isValidUUID(user.id)) {
          console.error('Invalid user ID format:', user.id);
          await useAuthStore.getState().logout();
          return;
        }

        set(state => {
          const newFolders = [...state.folders];
          const [movedFolder] = newFolders.splice(fromIndex, 1);
          newFolders.splice(toIndex, 0, movedFolder);
          
          // Update order property for each folder
          const updatedFolders = newFolders.map((folder, index) => ({
            ...folder,
            order: index
          }));
          
          return { folders: updatedFolders };
        });

        // Update order in Supabase
        try {
          const folders = get().folders;
          
          // Update each folder's order in Supabase
          await Promise.all(
            folders.map(async (folder, index) => {
              const { error } = await supabase
                .from('folders')
                .update({ order: index })
                .eq('id', folder.id);
                
              if (error) throw error;
            })
          );
        } catch (error) {
          console.error('Failed to update folder order:', error);
        }
      }
    }),
    {
      name: 'folder-storage',
      partialize: (state) => ({
        currentFolderId: state.currentFolderId
      })
    }
  )
);